// PipeConfigModal.jsx - Configuration modal for pipe elements
import React, { useState } from 'react';
import { X, GitBranch, Play, ArrowRight, ArrowLeft, ArrowLeftRight } from 'lucide-react';
import { PIPE_STYLES, DEFAULT_PIPE } from './PipeElement';

export default function PipeConfigModal({
  pipe,
  onSave,
  onClose,
  darkMode = false
}) {
  const [config, setConfig] = useState({
    width: pipe?.width || DEFAULT_PIPE.width,
    color: pipe?.color || DEFAULT_PIPE.color,
    style: pipe?.style || DEFAULT_PIPE.style,
    flowEnabled: pipe?.flowEnabled || DEFAULT_PIPE.flowEnabled,
    flowSpeed: pipe?.flowSpeed || DEFAULT_PIPE.flowSpeed,
    flowDirection: pipe?.flowDirection || DEFAULT_PIPE.flowDirection,
    flowColor: pipe?.flowColor || DEFAULT_PIPE.flowColor
  });
  const [displayTrend, setDisplayTrend] = useState(pipe?.displayTrend || false);

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave({
      ...pipe,
      ...config,
      displayTrend
    });
  };

  const bgColor = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#e5e7eb' : '#111827';
  const borderColor = darkMode ? '#334155' : '#e5e7eb';
  const inputBg = darkMode ? '#0f172a' : '#f8fafc';
  const accentColor = darkMode ? '#3b82f6' : '#2563eb';

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: `1px solid ${borderColor}`,
    backgroundColor: inputBg,
    color: textColor,
    fontSize: '14px',
    outline: 'none'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: darkMode ? '#94a3b8' : '#64748b',
    marginBottom: '4px'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: bgColor,
        borderRadius: '12px',
        width: '400px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${borderColor}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GitBranch size={20} color={accentColor} />
            <h3 style={{ margin: 0, color: textColor, fontSize: '16px', fontWeight: '600' }}>
              Configure Pipe
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex'
            }}
          >
            <X size={20} color={darkMode ? '#94a3b8' : '#64748b'} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Pipe Style */}
          <div>
            <label style={labelStyle}>Pipe Style</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {Object.entries(PIPE_STYLES).map(([key, styleInfo]) => {
                const isActive = config.style === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleChange('style', key)}
                    style={{
                      padding: '12px 8px',
                      border: `2px solid ${isActive ? accentColor : borderColor}`,
                      borderRadius: '8px',
                      backgroundColor: isActive ? (darkMode ? '#1e3a5f' : '#dbeafe') : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {/* Pipe preview */}
                    <svg width="60" height="20" viewBox="0 0 60 20">
                      <defs>
                        <linearGradient id={`preview-${key}`} x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor={adjustColor(config.color, 30)} />
                          <stop offset="50%" stopColor={config.color} />
                          <stop offset="100%" stopColor={adjustColor(config.color, -30)} />
                        </linearGradient>
                      </defs>
                      {styleInfo.layers >= 2 && (
                        <line x1="5" y1="10" x2="55" y2="10"
                          stroke={adjustColor(config.color, -30)}
                          strokeWidth="16"
                          strokeLinecap="round"
                        />
                      )}
                      {styleInfo.layers >= 3 && (
                        <line x1="5" y1="10" x2="55" y2="10"
                          stroke={darkMode ? '#374151' : '#d1d5db'}
                          strokeWidth="12"
                          strokeLinecap="round"
                        />
                      )}
                      <line x1="5" y1="10" x2="55" y2="10"
                        stroke={`url(#preview-${key})`}
                        strokeWidth={styleInfo.layers >= 2 ? 10 : 14}
                        strokeLinecap="round"
                      />
                      <line x1="5" y1="10" x2="55" y2="10"
                        stroke={darkMode ? '#1e293b' : '#e5e7eb'}
                        strokeWidth={14 * styleInfo.innerRatio}
                        strokeLinecap="round"
                      />
                    </svg>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      color: isActive ? accentColor : textColor
                    }}>
                      {styleInfo.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Thickness */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <label style={labelStyle}>Thickness</label>
              <span style={{ fontSize: '11px', color: darkMode ? '#94a3b8' : '#64748b' }}>
                {config.width}px
              </span>
            </div>
            <input
              type="range"
              min="10"
              max="40"
              step="2"
              value={config.width}
              onChange={(e) => handleChange('width', parseInt(e.target.value))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                appearance: 'none',
                backgroundColor: borderColor,
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Pipe Color */}
          <div>
            <label style={labelStyle}>Pipe Color</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="color"
                value={config.color}
                onChange={(e) => handleChange('color', e.target.value)}
                style={{
                  width: '48px',
                  height: '36px',
                  padding: '2px',
                  borderRadius: '6px',
                  border: `1px solid ${borderColor}`,
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={config.color}
                onChange={(e) => handleChange('color', e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
            </div>
          </div>

          {/* Flow Animation Section */}
          <div style={{
            padding: '16px',
            backgroundColor: darkMode ? '#0f172a' : '#f1f5f9',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Play size={16} color={accentColor} />
              <span style={{ fontSize: '14px', fontWeight: '500', color: textColor }}>
                Flow Animation
              </span>
              <div style={{ flex: 1 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.flowEnabled}
                  onChange={(e) => handleChange('flowEnabled', e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '12px', color: textColor }}>Enable</span>
              </label>
            </div>

            {config.flowEnabled && (
              <>
                {/* Flow Direction */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Flow Direction</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[
                      { value: 'forward', icon: ArrowRight, label: 'Forward' },
                      { value: 'reverse', icon: ArrowLeft, label: 'Reverse' },
                      { value: 'bidirectional', icon: ArrowLeftRight, label: 'Both' }
                    ].map(dir => {
                      const Icon = dir.icon;
                      const isActive = config.flowDirection === dir.value;
                      return (
                        <button
                          key={dir.value}
                          onClick={() => handleChange('flowDirection', dir.value)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            border: `1px solid ${isActive ? accentColor : borderColor}`,
                            borderRadius: '6px',
                            backgroundColor: isActive ? (darkMode ? '#1e3a5f' : '#dbeafe') : 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px'
                          }}
                        >
                          <Icon size={14} color={isActive ? accentColor : textColor} />
                          <span style={{ fontSize: '11px', color: isActive ? accentColor : textColor }}>
                            {dir.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Flow Speed */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <label style={labelStyle}>Flow Speed</label>
                    <span style={{ fontSize: '11px', color: darkMode ? '#94a3b8' : '#64748b' }}>
                      {config.flowSpeed.toFixed(1)}x
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.5"
                    value={config.flowSpeed}
                    onChange={(e) => handleChange('flowSpeed', parseFloat(e.target.value))}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      appearance: 'none',
                      backgroundColor: borderColor,
                      cursor: 'pointer'
                    }}
                  />
                </div>

                {/* Flow Color */}
                <div>
                  <label style={labelStyle}>Flow Indicator Color</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="color"
                      value={config.flowColor}
                      onChange={(e) => handleChange('flowColor', e.target.value)}
                      style={{
                        width: '48px',
                        height: '36px',
                        padding: '2px',
                        borderRadius: '6px',
                        border: `1px solid ${borderColor}`,
                        cursor: 'pointer'
                      }}
                    />
                    <input
                      type="text"
                      value={config.flowColor}
                      onChange={(e) => handleChange('flowColor', e.target.value)}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 20px',
          borderTop: `1px solid ${borderColor}`
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={displayTrend}
              onChange={(e) => setDisplayTrend(e.target.checked)}
              style={{ width: '15px', height: '15px' }}
            />
            <span style={{ fontSize: '13px', fontWeight: '500', color: textColor }}>Display Trend on Click</span>
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: `1px solid ${borderColor}`,
                backgroundColor: 'transparent',
                color: textColor,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: accentColor,
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Apply Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to adjust color brightness
function adjustColor(color, amount) {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const num = parseInt(hex, 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
  }
  return color;
}
